import React, { useContext, useEffect, useState } from "react";
import SessionContext from "SessionContext";
import { useHistory } from "react-router-dom";
import MenuLinks from "./admin-location-partials/menu-links";
import SystemIcons from "./admin-location-partials/system-icons";
import LocationTypes from "./admin-location-partials/location-types";
import CompanyIcons from "./admin-location-partials/company-icons";
import CompanyLocationTypes from "./admin-location-partials/company-location-types";
import ConfirmSuccess from "../core/ui/confirm-success";
import LocationRebuildNotice from "../core/ui/location-rebuild-notice";

const REBUILD_TRIGGERING_LOCATION_TYPE_FIELDS = ["name", "default_radius"];

function locationTypeChangeRequiresRebuild(original, updated) {
  if (!original) return false;
  return REBUILD_TRIGGERING_LOCATION_TYPE_FIELDS.some(
    (field) => original[field] !== updated[field]
  );
}

export default function AdminLocationTypes() {
  const session = useContext(SessionContext);

  const [companyList, setCompanyList] = useState([]);
  const [companyListLoaded, setCompanyListLoaded] = useState(false);

  const [svgIconList, setSvgIconList] = useState([]);
  const [svgIconListLoaded, setSvgIconListLoaded] = useState(false);

  const [companyIconLists, setCompanyIconLists] = useState({});

  const [locationTypeList, setLocationTypeList] = useState();
  const [locationTypeListLoaded, setLocationTypeListLoaded] = useState(false);

  const [companyLocationTypeLists, setCompanyLocationTypeLists] = useState({});

  const [creatingNewSysIcon, setCreatingNewSysIcon] = useState(false);
  const [newIcon, setNewIcon] = useState({});

  const [newIconCompany, setNewIconCompany] = useState();
  const [creatingNewCompanyIcon, setCreatingNewCompanyIcon] = useState(false);
  const [newCompanyIcon, setNewCompanyIcon] = useState({});

  const [creatingNewSysLocationType, setCreatingNewSysLocationType] =
    useState(false);
  const [newLocationType, setNewLocationType] = useState({});

  const [newLocationTypeCompany, setNewLocationTypeCompany] = useState();
  const [creatingNewCompanyLocationType, setCreatingNewCompanyLocationType] =
    useState(false);
  const [newCompanyLocationType, setNewCompanyLocationType] = useState({});

  const [editingSysIcon, setEditingSysIcon] = useState(false);
  const [sysIconToEdit, setSysIconToEdit] = useState({});

  const [editingCompanyIcon, setEditingCompanyIcon] = useState(false);
  const [companyIconToEdit, setCompanyIconToEdit] = useState({});

  const [editingSysLocationType, setEditingSysLocationType] = useState(false);
  const [sysLocationTypeToEdit, setSysLocationTypeToEdit] = useState({});
  const [sysLocationTypeOriginal, setSysLocationTypeOriginal] = useState(null);

  const [editingCompanyLocationType, setEditingCompanyLocationType] =
    useState(false);
  const [companyLocationTypeToEdit, setCompanyLocationTypeToEdit] = useState(
    {}
  );
  const [companyLocationTypeOriginal, setCompanyLocationTypeOriginal] = useState(null);
  const [showRebuildNotice, setShowRebuildNotice] = useState(false);
  const [rebuildNoticeCompanyId, setRebuildNoticeCompanyId] = useState(null);

  const [showSystemIcons, setShowSystemIcons] = useState(false);
  const [showSystemLocationTypes, setShowSystemLocationTypes] = useState(false);
  const [showCompanyIcons, setShowCompanyIcons] = useState(false);
  const [showCompanyLocationTypes, setShowCompanyLocationTypes] =
    useState(false);
  const [showLinksMenu, setShowLinksMenu] = useState(true);
  const [systemIconsKey, setSystemIconsKey] = useState(true);
  const [successLabel, setSuccessLabel] = useState("Updated Successfully.");
  const [showSuccess, setShowSuccess] = useState(false);

  function loadCompanyList() {
    session
      .query("companies/getAll")
      .then((response) => {
        if (!Array.isArray(response)) return;
        setCompanyList(response);
        setCompanyListLoaded(true);
      })
      .catch((error) => {
        console.log("Load company list failed", JSON.stringify(error));
      });
  }

  function loadLocationTypes() {
    session
      .query("location/location-types", { companyId: 0 })
      .then((response) => {
        if (!Array.isArray(response)) return;
        setLocationTypeList(response);
        setLocationTypeListLoaded(true);
      })
      .catch((error) => {
        console.log("Load location types failed", JSON.stringify(error));
      });
  }

  function loadSvgIcons() {
    session
      .query("location/svg-icons", { companyId: 0 })
      .then((response) => {
        if (!Array.isArray(response)) return;
        setSvgIconList(response);
        setSvgIconListLoaded(true);
      })
      .catch((error) => {
        console.log("Load SVG icons failed", JSON.stringify(error));
      });
  }

  useEffect(() => {
    loadCompanyList();
    loadLocationTypes();
    loadSvgIcons();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function loadCompanyLocationTypes(company) {
    session
      .query("location/location-types", { companyId: company.id })
      .then((response) => {
        if (!Array.isArray(response)) return;
        setCompanyLocationTypeLists((prevState) => ({
          ...prevState,
          [company.id.toString()]: response.filter(
            (locType) => locType.company_id === company.id
          ),
        }));
      })
      .catch((error) => {
        console.log("Load company location types failed", JSON.stringify(error));
      });
  }

  function loadCompanyIcons(company) {
    session
      .query("location/svg-icons", { companyId: company.id })
      .then((response) => {
        if (!Array.isArray(response)) return;
        setCompanyIconLists((prevState) => ({
          ...prevState,
          [company.id.toString()]: response,
        }));
      })
      .catch((error) => {
        console.log("Load company icons failed", JSON.stringify(error));
      });
  }

  function persistIcon(svgIcon) {
    const xhrConfig = {};
    session
      .command("location/persist-svgIcon", svgIcon, xhrConfig)
      .then(() => {
        setSuccessLabel(svgIcon.id ? "Icon Updated Successfully." : "Icon Created Successfully.");
        setShowSuccess(true);
        setTimeout(() => {
          setShowSuccess(false);
          loadSvgIcons();
          if (svgIcon.company_id && svgIcon.company_id !== 0) {
            const company = getCompany(svgIcon.company_id);
            if (company) loadCompanyIcons(company);
          }
        }, 2000);
      })
      .catch((error) => {
        console.log("Icon Create Failed", JSON.stringify(error));
      });
  }

  function deleteIcon(svgIcon) {
    const xhrConfig = {};
    session
      .command("location/delete-svgIcon", svgIcon, xhrConfig)
      .then(() => {
        setSuccessLabel("Icon Deleted Successfully.");
        setShowSuccess(true);
        setTimeout(() => {
          setShowSuccess(false);
          loadSvgIcons();
          if (svgIcon.company_id && svgIcon.company_id !== 0) {
            const company = getCompany(svgIcon.company_id);
            if (company) loadCompanyIcons(company);
          }
          setEditingSysIcon(false);
        }, 2000);
      })
      .catch((error) => {
        console.log("Icon deleted Failed", JSON.stringify(error));
      });
  }

  function saveNewIcon(useCompany = false) {
    let svgIcon = {};
    svgIcon.id = null;

    if (useCompany && newIconCompany !== undefined) {
      svgIcon.company_id = newIconCompany.id;
      svgIcon.name = newCompanyIcon.name;
      svgIcon.svg = newCompanyIcon.svg;
    } else {
      svgIcon.company_id = 0;
      svgIcon.name = newIcon.name;
      svgIcon.svg = newIcon.svg;
    }
    persistIcon(svgIcon);
  }

  function saveNewCompanyIcon() {
    saveNewIcon(true);
  }

  function handleSuccessAction(companyId = null) {
    setShowSuccess(true);
    setTimeout(() => {
      setShowSuccess(false);
      loadLocationTypes();
      if (companyId && companyId !== 0) {
        const company = getCompany(companyId);
        if (company) loadCompanyLocationTypes(company);
      }
    }, 2000);
  }

  function persistLocationType(locationType, notifyRebuild = false) {
    const xhrConfig = {};
    session
      .command("location/persist-location-type", locationType, xhrConfig)
      .then(() => {
        setSuccessLabel(locationType.id ? "Location Type Updated Successfully." : "Location Type Created Successfully.");
        if (notifyRebuild) {
          setRebuildNoticeCompanyId(locationType.company_id);
          setShowRebuildNotice(true);
        } else {
          handleSuccessAction(locationType.company_id);
        }
      })
      .catch((error) => {
        console.log("Location Type create/update Failed", JSON.stringify(error));
      });
  }

  function onCloseRebuildNotice() {
    setShowRebuildNotice(false);
    loadLocationTypes();
    if (rebuildNoticeCompanyId && rebuildNoticeCompanyId !== 0) {
      const company = getCompany(rebuildNoticeCompanyId);
      if (company) loadCompanyLocationTypes(company);
    }
  }

  function deleteLocationType(locationType) {
    const xhrConfig = {};
    session
      .command("location/delete-location-type", locationType, xhrConfig)
      .then(() => {
        setSuccessLabel("Location Type Deleted Successfully.");
        handleSuccessAction(locationType.company_id);
      })
      .catch((error) => {
        console.log("Location Type deleted Failed", JSON.stringify(error));
      });
  }

  function saveNewLocationType(useCompany = false) {
    let locationType = {};
    locationType.id = null;
    if (useCompany && newLocationTypeCompany !== undefined) {
      locationType.company_id = newLocationTypeCompany.id;
      locationType.name = newCompanyLocationType.name;
      locationType.svg_icon_id = newCompanyLocationType.svg_icon_id;
      locationType.color = newCompanyLocationType.color;
      locationType.default_radius = newCompanyLocationType.default_radius;
    } else {
      locationType.company_id = 0;
      locationType.name = newLocationType.name;
      locationType.svg_icon_id = newLocationType.svg_icon_id;
      locationType.color = newLocationType.color;
      locationType.default_radius = newLocationType.default_radius;
    }

    persistLocationType(locationType);
  }

  function newSystemLocationType(ev) {
    saveNewLocationType();
  }
  function saveNewCompanyLocationType(ev) {
    saveNewLocationType(true);
  }

  function setSysIconName(ev) {
    setNewIcon((prevState) => ({ ...prevState, name: ev.target.value }));
  }
  function setCompanyIconName(ev) {
    setNewCompanyIcon((prevState) => ({ ...prevState, name: ev.target.value }));
  }
  function setSysIconSvg(ev) {
    setNewIcon((prevState) => ({ ...prevState, svg: ev.target.value }));
  }

  function setCompanyIconSvg(ev) {
    setNewCompanyIcon((prevState) => ({ ...prevState, svg: ev.target.value }));
  }

  function editSvgIcon(svgIcon) {
    if (svgIcon.company_id === undefined || svgIcon.company_id === null) {
      // editing system icons
      setSysIconToEdit(svgIcon);
      setEditingSysIcon(true);
    } else {
      // editing company icons
      setCompanyIconToEdit(svgIcon);
      setEditingCompanyIcon(true);
    }
  }

  function setSysIconToEditName(ev) {
    setSysIconToEdit((prevState) => ({ ...prevState, name: ev.target.value }));
  }

  function setSysIconToEditSvg(ev) {
    setSysIconToEdit((prevState) => ({ ...prevState, svg: ev.target.value }));
  }

  function updateSysIconToEdit() {
    if (
      sysIconToEdit.company_id === undefined ||
      sysIconToEdit.company_id === null
    ) {
      sysIconToEdit.company_id = 0;
    }
    persistIcon(sysIconToEdit);
    setEditingSysIcon(false);
  }

  function setCompanyIconToEditName(ev) {
    setCompanyIconToEdit((prevState) => ({
      ...prevState,
      name: ev.target.value,
    }));
  }

  function setCompanyIconToEditSvg(ev) {
    setCompanyIconToEdit((prevState) => ({
      ...prevState,
      svg: ev.target.value,
    }));
  }

  function updateCompanyIconToEdit() {
    persistIcon(companyIconToEdit);
    setEditingCompanyIcon(false);
  }

  function setLocationTypeName(ev) {
    setNewLocationType((prevState) => ({
      ...prevState,
      name: ev.target.value,
    }));
  }

  function setLocationTypeColor(ev) {
    setNewLocationType((prevState) => ({
      ...prevState,
      color: ev.target.value,
    }));
  }

  function setLocationTypeDefaultRadius(ev) {
    setNewLocationType((prevState) => ({
      ...prevState,
      default_radius: ev.target.value,
    }));
  }

  function setCompanyLocationTypeName(ev) {
    setNewCompanyLocationType((prevState) => ({
      ...prevState,
      name: ev.target.value,
    }));
  }

  function setCompanyLocationTypeColor(ev) {
    setNewCompanyLocationType((prevState) => ({
      ...prevState,
      color: ev.target.value,
    }));
  }
  function setCompanyLocationTypeSvgIconId(value) {
    setNewCompanyLocationType((prevState) => ({
      ...prevState,
      svg_icon_id: value,
    }));
  }

  function setCompanyLocationTypeDefaultRadius(ev) {
    setNewCompanyLocationType((prevState) => ({
      ...prevState,
      default_radius: ev.target.value,
    }));
  }

  function editLocationType(locationType) {
    if (
      locationType.company_id === undefined ||
      locationType.company_id === null
    ) {
      // editing system location type
      setSysLocationTypeToEdit(locationType);
      setSysLocationTypeOriginal({ ...locationType });
      setEditingSysLocationType(true);
    } else {
      // editing company location type
      setCompanyLocationTypeToEdit(locationType);
      setCompanyLocationTypeOriginal({ ...locationType });
      setEditingCompanyLocationType(true);
    }
  }

  function setSysLocationTypeToEditName(ev) {
    setSysLocationTypeToEdit((prevState) => ({
      ...prevState,
      name: ev.target.value,
    }));
  }

  function setSysLocationTypeToEditColor(ev) {
    setSysLocationTypeToEdit((prevState) => ({
      ...prevState,
      color: ev.target.value,
    }));
  }

  function setSysLocationTypeToEditDefaultRadius(ev) {
    setSysLocationTypeToEdit((prevState) => ({
      ...prevState,
      default_radius: ev.target.value,
    }));
  }

  function onChangeSysLocationTypeToEditIcon(ev) {
    setSysLocationTypeToEdit((prevState) => ({
      ...prevState,
      svg_icon_id: ev.target.value,
    }));
  }

  function updateSysLocationTypeToEdit() {
    if (
      sysLocationTypeToEdit.company_id === undefined ||
      sysLocationTypeToEdit.company_id === null
    ) {
      sysLocationTypeToEdit.company_id = 0;
    }
    const notifyRebuild = locationTypeChangeRequiresRebuild(
      sysLocationTypeOriginal,
      sysLocationTypeToEdit
    );
    setEditingSysLocationType(false);
    persistLocationType(sysLocationTypeToEdit, notifyRebuild);
  }

  function setCompanyLocationTypeToEditName(ev) {
    setCompanyLocationTypeToEdit((prevState) => ({
      ...prevState,
      name: ev.target.value,
    }));
  }

  function setCompanyLocationTypeToEditColor(ev) {
    setCompanyLocationTypeToEdit((prevState) => ({
      ...prevState,
      color: ev.target.value,
    }));
  }

  function setCompanyLocationTypeToEditDefaultRadius(ev) {
    setCompanyLocationTypeToEdit((prevState) => ({
      ...prevState,
      default_radius: ev.target.value,
    }));
  }

  function onChangeCompanyLocationTypeToEditIcon(ev) {
    setCompanyLocationTypeToEdit((prevState) => ({
      ...prevState,
      svg_icon_id: ev.target.value,
    }));
  }

  function updateCompanyLocationTypeToEdit() {
    const notifyRebuild = locationTypeChangeRequiresRebuild(
      companyLocationTypeOriginal,
      companyLocationTypeToEdit
    );
    setEditingCompanyLocationType(false);
    persistLocationType(companyLocationTypeToEdit, notifyRebuild);
  }

  function getCompany(id) {
    const company = session.appState.identity.assigned_companies.find(
      (c) => c.id.toString() === id.toString()
    );
    return company;
  }

  function onChangeIconCompany(ev) {
    const company = getCompany(ev.target.value);
    if (company) {
      setNewIconCompany(company);
    }
  }

  function onChangeLocationTypeCompany(ev) {
    const company = getCompany(ev.target.value);
    if (company) {
      setNewLocationTypeCompany(company);
      loadCompanyIcons(company);
    }
  }

  function onChangeLocationTypeIcon(event) {
    setNewLocationType((prevState) => ({
      ...prevState,
      svg_icon_id: event.target.value,
    }));
  }

  function onChangeCompanyLocationTypeIcon(event) {
    setCompanyLocationTypeSvgIconId(event.target.value);
  }

  function newSysIcon() {
    setCreatingNewSysIcon((prev) => !prev);
  }

  function newSysLocationTypeToggle() {
    setCreatingNewSysLocationType((prev) => !prev);
  }

  function newCompanyIconToggle() {
    setCreatingNewCompanyIcon((prev) => !prev);
  }

  function newCompanyLocationTypeToggle() {
    setCreatingNewCompanyLocationType((prev) => !prev);
  }

  function toggleShowSystemIcons() {
    setShowSystemIcons((prev) => !prev);
    setShowLinksMenu((prev) => !prev);
  }

  function toggleShowSystemLocationTypes() {
    setShowSystemLocationTypes((prev) => !prev);
    setShowLinksMenu((prev) => !prev);
  }

  function toggleShowCompanyIcons() {
    setShowCompanyIcons((prev) => !prev);
    setShowLinksMenu((prev) => !prev);
  }

  function toggleShowCompanyLocationTypes() {
    setShowCompanyLocationTypes((prev) => !prev);
    setShowLinksMenu((prev) => !prev);
  }

  const history = useHistory();
  function onClickAdmin(ev) {
    ev.stopPropagation();
    history.push("/admin");
  }

  const onCancel = () => {
    setCreatingNewSysIcon(false);
    setEditingSysIcon(false);
    setCreatingNewSysLocationType(false);
    setEditingSysLocationType(false);
    setCreatingNewCompanyIcon(false);
    setEditingCompanyIcon(false);
    setCreatingNewCompanyLocationType(false);
    setEditingCompanyLocationType(false);
    setSystemIconsKey(!systemIconsKey);
    setCompanyIconToEdit("");
  };

  return (
    <div className="page admin-page admin-location-types-page">
      <div className="list-header">
        <button className="new-button" onClick={onClickAdmin}>
          <i className="bi bi-chevron-left"></i> Admin
        </button>
        <h1>Location Types and Icons</h1>
        <i className="spacer" />
      </div>

      {showLinksMenu && (
        <MenuLinks
          toggleSystemIcons={toggleShowSystemIcons}
          toggleSystemLocations={toggleShowSystemLocationTypes}
          toggleCompanyIcons={toggleShowCompanyIcons}
          toggleCompanyLocations={toggleShowCompanyLocationTypes}
        />
      )}

      <div className="location-types-main-wrapper">
      {svgIconListLoaded && showSystemIcons && (
        <SystemIcons
          backBtn={toggleShowSystemIcons}
          listOfIcons={svgIconList}
          editingIcon={editingSysIcon}
          creatingIcon={creatingNewSysIcon}
          onCreateNew={newSysIcon}
          newName={setSysIconName}
          newSvg={setSysIconSvg}
          newSave={saveNewIcon}
          cancel={onCancel}
          editIconInfo={sysIconToEdit}
          editName={setSysIconToEditName}
          editSvg={setSysIconToEditSvg}
          editSave={updateSysIconToEdit}
          deleteIcon={deleteIcon}
          editSvgIcon={editSvgIcon}
          key={systemIconsKey}
        />
      )}

      {svgIconListLoaded &&
        locationTypeListLoaded &&
        showSystemLocationTypes && (
          <LocationTypes
            toggleShowSystemLocationTypes={toggleShowSystemLocationTypes}
            locationTypeList={locationTypeList}
            deleteLocationType={deleteLocationType}
            editLocationType={editLocationType}
            editingSysLocationType={editingSysLocationType}
            newSysLocationTypeToggle={newSysLocationTypeToggle}
            creatingNewSysLocationType={creatingNewSysLocationType}
            setLocationTypeName={setLocationTypeName}
            setLocationTypeColor={setLocationTypeColor}
            setLocationTypeDefaultRadius={setLocationTypeDefaultRadius}
            onChangeLocationTypeIcon={onChangeLocationTypeIcon}
            svgIconList={svgIconList}
            onCancel={onCancel}
            newSystemLocationType={newSystemLocationType}
            sysLocationTypeToEdit={sysLocationTypeToEdit}
            setSysLocationTypeToEditName={setSysLocationTypeToEditName}
            setSysLocationTypeToEditColor={setSysLocationTypeToEditColor}
            setSysLocationTypeToEditDefaultRadius={
              setSysLocationTypeToEditDefaultRadius
            }
            onChangeSysLocationTypeToEditIcon={
              onChangeSysLocationTypeToEditIcon
            }
            updateSysLocationTypeToEdit={updateSysLocationTypeToEdit}
          />
        )}

      {companyListLoaded && showCompanyIcons && (
        <CompanyIcons
          companyList={companyList}
          companyIconLists={companyIconLists}
          newCompanyIconToggle={newCompanyIconToggle}
          editingCompanyIcon={editingCompanyIcon}
          creatingNewCompanyIcon={creatingNewCompanyIcon}
          onChangeIconCompany={onChangeIconCompany}
          assignedCompanies={session.appState.identity.assigned_companies}
          setCompanyIconName={setCompanyIconName}
          setCompanyIconSvg={setCompanyIconSvg}
          saveNewCompanyIcon={saveNewCompanyIcon}
          companyIconToEdit={companyIconToEdit}
          setCompanyIconToEditName={setCompanyIconToEditName}
          setCompanyIconToEditSvg={setCompanyIconToEditSvg}
          updateCompanyIconToEdit={updateCompanyIconToEdit}
          backBtn={toggleShowCompanyIcons}
          cancel={onCancel}
          editSvgIcon={editSvgIcon}
          deleteIcon={deleteIcon}
          onSelectCompany={loadCompanyIcons}
        />
      )}

      {companyListLoaded && svgIconListLoaded && showCompanyLocationTypes && (
        <CompanyLocationTypes
          companyList={companyList}
          companyLocationTypeLists={companyLocationTypeLists}
          newCompanyLocationTypeToggle={newCompanyLocationTypeToggle}
          editingCompanyLocationType={editingCompanyLocationType}
          creatingNewCompanyLocationType={creatingNewCompanyLocationType}
          onChangeLocationTypeCompany={onChangeLocationTypeCompany}
          assignedCompanies={session.appState.identity.assigned_companies}
          setCompanyLocationTypeName={setCompanyLocationTypeName}
          setCompanyLocationTypeColor={setCompanyLocationTypeColor}
          setCompanyLocationTypeDefaultRadius={
            setCompanyLocationTypeDefaultRadius
          }
          newLocationTypeCompany={newLocationTypeCompany}
          onChangeCompanyLocationTypeIcon={onChangeCompanyLocationTypeIcon}
          svgIconList={svgIconList}
          companyIconLists={companyIconLists}
          saveNewCompanyLocationType={saveNewCompanyLocationType}
          svgIconListLoaded={svgIconListLoaded}
          companyLocationTypeToEdit={companyLocationTypeToEdit}
          setCompanyLocationTypeToEditName={
            setCompanyLocationTypeToEditName
          }
          setCompanyLocationTypeToEditColor={
            setCompanyLocationTypeToEditColor
          }
          setCompanyLocationTypeToEditDefaultRadius={
            setCompanyLocationTypeToEditDefaultRadius
          }
          onChangeCompanyLocationTypeToEditIcon={
            onChangeCompanyLocationTypeToEditIcon
          }
          updateCompanyLocationTypeToEdit={updateCompanyLocationTypeToEdit}
          deleteLocationType={deleteLocationType}
          editLocationType={editLocationType}
          cancel={onCancel}
          backBtn={toggleShowCompanyLocationTypes}
          onSelectCompany={(company) => {
            loadCompanyLocationTypes(company);
            loadCompanyIcons(company);
          }}
        />
      )}
      </div>
      <ConfirmSuccess show={showSuccess && !showRebuildNotice} label={successLabel} />
      <LocationRebuildNotice show={showRebuildNotice} label={successLabel} onClose={onCloseRebuildNotice} />
    </div>
  );
}
